// react core
import { useContext, useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";

// theming
import mainTheme from "components/main/MainTheme";
import { ThemeProvider, responsiveFontSizes } from "@mui/material/styles";

// material design
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";

// fonts and icons
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUser as iconUser } from "@fortawesome/pro-thin-svg-icons";
import { faCommentAltDots as iconMessaging } from "@fortawesome/pro-thin-svg-icons";
import { faCalendarCheck as iconCalendar } from "@fortawesome/pro-duotone-svg-icons";

// entzy pages
import MemberProfile from "./MemberProfile";

// entzy config and services
import configEntzy from "components/config/ConfigEntzy";
import { MainContext } from "components/main/MainContext";
import { BigConnectButton } from "components/utils/common/CommonButtons";
import { ModuleHeaderTag } from "components/utils/common/CommonTags";
import { scrollSnapTop } from "services/tools";

// entzy hooks
import { useMainRedirect } from "hooks/main/mainRedirect";

function MemberIndexPublic(props) {
  // if not connected capture url for redirect on connect
  useMainRedirect(props);

  let { member } = useParams();
  const displayName = "@" + (member ? member.replace(/@/g, "") : "guest");

  return (
    <Box className="box-default">
      <Box className="box-default">
        <Typography
          variant="h6"
          sx={{
            mt: configEntzy.APP_SPACING_MD,
          }}
        >
          <FontAwesomeIcon icon={iconUser} transform="left-10" color="white" />
          <FontAwesomeIcon icon={iconMessaging} size="2x" color="orange" />
          <FontAwesomeIcon icon={iconUser} transform="right-10" color="white" />
        </Typography>
        <Typography
          variant="h6"
          noWrap={true}
          sx={{
            mt: configEntzy.APP_SPACING_MD,
          }}
          color="primary"
        >
          Welcome
        </Typography>
        <Typography
          variant="h6"
          noWrap={true}
          sx={{
            mt: configEntzy.APP_SPACING_SM,
          }}
          color="white"
        >
          <span>Member Connections</span>
        </Typography>
      </Box>

      {member ? (
        <Box className="box-default">
          <Box
            className="box-default"
            sx={{
              mt: configEntzy.APP_SPACING_MD2X,
              p: configEntzy.APP_SPACING_MD,
            }}
          >
            <Divider />
          </Box>
          <Box className="box-default">
            <ModuleHeaderTag text="Connecting with:" />
            <ModuleHeaderTag text={displayName} />
          </Box>
          <Box
            className="box-default"
            sx={{
              p: configEntzy.APP_SPACING_MD,
            }}
          >
            <Divider />
          </Box>
        </Box>
      ) : (
        <Box
          className="box-default text-paragraph"
          sx={{
            mt: configEntzy.APP_SPACING_MD2X,
            mb: configEntzy.APP_SPACING_MD2X,
          }}
        >
          <Container maxWidth="sm">
            <Typography
              variant="h6"
              color="white"
              sx={{ mt: configEntzy.APP_SPACING_SM }}
            >
              Use a member profile link if you have one or connect to browse
              activity in your area
            </Typography>
          </Container>
        </Box>
      )}
      <BigConnectButton
        {...props}
        headerIcon={iconCalendar}
        headerTitle={member ? "Let's Meet" : "Let's Eventuate"}
        headerSubTitle="Connect to entzy to continue"
        boxSx={{ mt: configEntzy.APP_SPACING_MD2X }}
      />
    </Box>
  );
}

function MemberIndexConnected(props) {
  return (
    <Box className="box-default">
      <MemberProfile {...props} />
    </Box>
  );
}

function MemberIndex(props) {
  const navigate = props.navigate;

  let userConnected = props.user.connected;
  let userName = props.user.name;
  let entryPage = props.page;

  let { member } = useParams();
  let memberOverride = props.member;

  const mainContext = useContext(MainContext);

  const [memberName, setMemberName] = useState(null);
  // const [notesToSelf, setNotesToSelf] = useState(false);

  const handleSetMemberName = (name) => {
    let nameClean;
    if (name && name.length > 0) {
      nameClean = name.toLowerCase();
      nameClean = nameClean.replace(/@/g, "");
      nameClean = nameClean.replace(/ /g, "_");
      nameClean = nameClean.replace(/[^a-z0-9_-]/g, "");
      setMemberName(nameClean);
      if (!mainContext.state.memberMessaging.viewer.open) {
        scrollSnapTop();
        navigate("/@/" + nameClean);
      }
    } else {
      setMemberName(null);
    }
  };
  const cbHandleSetMemberName = useCallback(handleSetMemberName, [
    navigate,
    mainContext.state.memberMessaging.viewer.open,
  ]);

  useEffect(() => {
    if (userConnected) {
      if (entryPage === "/members/me") {
        cbHandleSetMemberName(userName);
      } else if (memberOverride) {
        cbHandleSetMemberName(memberOverride);
      } else if (member) {
        cbHandleSetMemberName(member.replace(/@/g, ""));
      } else {
        cbHandleSetMemberName(null);
      }
    }
  }, [
    userConnected,
    userName,
    entryPage,
    member,
    memberOverride,
    cbHandleSetMemberName,
  ]);

  // useEffect(() => {
  //   if (userName === memberName) {
  //     setNotesToSelf(true);
  //   } else {
  //     setNotesToSelf(false);
  //   }
  // }, [userName, memberName]);

  // useEffect(() => {
  //   const matchBodyClass = () => {
  //     document.getElementById("anchor-member-area").className =
  //       "box-default " + document.body.className;
  //   };
  //   matchBodyClass();
  // }, []);

  // memberName ? (
  //   <MemberIndexConnected
  //     {...props}
  //     key={memberName}
  //     memberName={memberName}
  //     setMemberName={handleSetMemberName}
  //   />
  // ) : (
  //   <MemberIndexMenu
  //     {...props}
  //     setMemberName={handleSetMemberName}
  //   />
  // )

  return (
    <ThemeProvider theme={responsiveFontSizes(mainTheme)}>
      <Box id="anchor-member-area" className="box-default">
        <Box className="box-default">
          <Container maxWidth="md">
            {props.user.connected ? (
              <MemberIndexConnected
                {...props}
                key={memberName}
                memberName={memberName}
                setMemberName={handleSetMemberName}
              />
            ) : (
              <Box className="box-default">
                <MemberIndexPublic {...props} />
              </Box>
            )}
          </Container>
        </Box>
      </Box>
    </ThemeProvider>
  );
}

export default MemberIndex;
